import { useState } from "react";
import { Box, IconButton, Modal, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ArrowBackIcon from "@material-ui/icons/KeyboardArrowLeft";
import ArrowForwardIcon from "@material-ui/icons/KeyboardArrowRight";
import GetAppIcon from "@material-ui/icons/GetApp";
import { TGroupChatMessageFile } from "../../api/group_chat/get_history";
import { MESSAGE_FILE_TYPE_IMAGES } from "../../utils/chat/messageHistoryUtils";

type Props = {
  file: TGroupChatMessageFile;
  files: TGroupChatMessageFile[];
  className?: string;
  downloadFile: (file: TGroupChatMessageFile) => void;
};

const useStyles = makeStyles(() => ({
  modalRoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  backdrop: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "100%",
    outline: "none",
    background: "#fff",
  },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    borderBottom: "1px solid #e0e0e0",
  },
  fileName: {
    minWidth: 0,
    flex: 1,
    padding: "24px 0 24px 16px",
    fontSize: 16,
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerActions: {
    display: "flex",
    flexDirection: "row",
    flexShrink: 0,
    flexWrap: "nowrap",
    alignItems: "center",
  },
  iconButton: {
    color: "var(--main-color)",
    "&:hover": {
      background: "none",
    },
  },
  downloadButton: {
    color: "var(--main-color)",
    "&:hover": {
      background: "none",
    },
  },
  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--main-color)",
    "&:hover": {
      background: "none",
    },
  },
  prevButton: {
    left: 0,
  },
  nextButton: {
    right: 0,
  },
  previewImage: {
    maxWidth: "70vw",
    maxHeight: "70vh",
    objectFit: "contain",
  },
}));

const ChatMessageHistoryImage: React.FC<Props> = ({
  file,
  files,
  className,
  downloadFile,
}) => {
  const classes = useStyles();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [imgData, setImgData] = useState<TGroupChatMessageFile>(file);
  const imgFiles = files.filter((f) => f.file_type === MESSAGE_FILE_TYPE_IMAGES);

  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);

  return (
    <>
      <img
        alt={imgData.file_name}
        className={className}
        src={file.file_url}
        role="button"
        tabIndex={0}
        onClick={openPreview}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            openPreview();
          }
        }}
      />
      <Modal
        open={isPreviewOpen}
        onClose={closePreview}
        className={classes.modalRoot}
      >
        <Box className={classes.backdrop} onClick={closePreview}>
          <Box className={classes.header} onClick={(e) => e.stopPropagation()}>
            <Box className={classes.fileName}>{imgData.file_name}</Box>
            <Box className={classes.headerActions}>
              <IconButton
                onClick={() => downloadFile(imgData)}
                aria-label="ダウンロード"
                disableRipple
                className={classes.downloadButton}
              >
                <GetAppIcon style={{ fontSize: 32 }} />
              </IconButton>
              <IconButton
                aria-label="閉じる"
                onClick={closePreview}
                disableRipple
                className={classes.iconButton}
                style={{ marginRight: 8 }}
              >
                <CloseIcon style={{ fontSize: 32 }} />
              </IconButton>
            </Box>
          </Box>
          {imgFiles.length > 1 && (
            <IconButton
              aria-label="前の画像"
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = imgFiles.findIndex((f) => f.id === imgData.id);
                setImgData(imgFiles[currentIndex - 1] ?? imgFiles[0]);
              }}
              className={`${classes.navButton} ${classes.prevButton}`}
            >
              <ArrowBackIcon style={{ fontSize: 42 }} />
            </IconButton>
          )}
          <img
            src={imgData.file_url}
            alt={imgData.file_name}
            className={classes.previewImage}
            onClick={(e) => e.stopPropagation()}
          />
          {imgFiles.length > 1 && (
            <IconButton
              aria-label="次の画像"
              disableRipple
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = imgFiles.findIndex((f) => f.id === imgData.id);
                setImgData(imgFiles[currentIndex + 1] ?? imgFiles[0]);
              }}
              className={`${classes.navButton} ${classes.nextButton}`}
            >
              <ArrowForwardIcon style={{ fontSize: 42 }} />
            </IconButton>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ChatMessageHistoryImage;
